import {
  type Surface,
  SURFACE_GRIP, SURFACE_ACCEL, SURFACE_PROBABILITY, SURFACE_LENGTH_RANGE,
  RECOVERY_ASPHALT_PCT, RECOVERY_ASPHALT_RANGE, START_ASPHALT_M, SURFACE_TRANSITION_M,
  ICE_AHEAD_LOOK_M,
  CURVE_INTENSITY_RANGE, STRAIGHT_LENGTH_RANGE, TURN_LENGTH_RANGE, TURN_RAMP_M,
  CURVE_AHEAD_LOOK_M, CURVE_WARN_GRIP_MAX, CURVE_WARN_CURVATURE,
} from '../config.ts'

export type { Surface }

function hash(n: number): number {
  let x = (n + 0x9E3779B9) | 0
  x = Math.imul(x ^ (x >>> 16), 0x85EBCA6B)
  x = Math.imul(x ^ (x >>> 13), 0xC2B2AE35)
  x = x ^ (x >>> 16)
  return (x >>> 0) / 0x100000000
}

// ── Surface segments (variable length, recovery asphalt) ────────────────────

interface RoadSegment { start: number; end: number; surface: Surface }

const SURFACE_ORDER: Surface[] = ['asphalt', 'snow', 'ice', 'sand', 'mud']

function pickSurface(h: number): Surface {
  let acc = 0
  for (const s of SURFACE_ORDER) {
    acc += SURFACE_PROBABILITY[s]
    if (h < acc) return s
  }
  return 'asphalt'
}

let _segments: RoadSegment[] = []
let _generatedUpTo = 0
let _lastWasSpecial = false
let _seed = 0

/** Reset all road state with a new seed. Call at game start. */
export function resetRoad(seed: number): void {
  _seed = seed
  _segments = []
  _generatedUpTo = 0
  _lastWasSpecial = false
  _curves = []
  _curvesUpTo = 0
}

function ensureGenerated(upToDist: number): void {
  while (_generatedUpTo < upToDist + 500) {
    const idx = _segments.length
    if (idx === 0) {
      _segments.push({ start: 0, end: START_ASPHALT_M, surface: 'asphalt' })
      _generatedUpTo = START_ASPHALT_M
      _lastWasSpecial = false
      continue
    }

    // After a special surface → recovery asphalt (85% chance)
    if (_lastWasSpecial && hash(idx * 53 + 7 + _seed) < RECOVERY_ASPHALT_PCT) {
      const [minL, maxL] = RECOVERY_ASPHALT_RANGE
      const length = minL + (maxL - minL) * hash(idx * 37 + 19 + _seed)
      _segments.push({ start: _generatedUpTo, end: _generatedUpTo + length, surface: 'asphalt' })
      _generatedUpTo += length
      _lastWasSpecial = false
      continue
    }

    // Normal segment. Surfaces and curves are generated independently on purpose:
    // ice forms where water sits and the sun does not reach, which on a real road
    // is the inside of a bend more often than a straight. Forcing a flat entry was
    // tried and removed — it paved over a fifth of the route and made the game
    // arcade-ish. The promise to the player is not "hazards are never awkward", it
    // is "slow down enough and you can hold it", which controllability.test.ts pins.
    const surface = pickSurface(hash(idx * 17 + 3 + _seed))
    const [minLen, maxLen] = SURFACE_LENGTH_RANGE[surface]
    const length = minLen + (maxLen - minLen) * hash(idx * 31 + 11 + _seed)
    _segments.push({ start: _generatedUpTo, end: _generatedUpTo + length, surface })
    _generatedUpTo += length
    _lastWasSpecial = surface !== 'asphalt'
  }
}

export function getSurfaceAt(distanceMeters: number, _seed = 0): Surface {
  if (distanceMeters < 0) return 'asphalt'
  ensureGenerated(distanceMeters)
  for (let i = _segments.length - 1; i >= 0; i--) {
    if (distanceMeters >= _segments[i]!.start) return _segments[i]!.surface
  }
  return 'asphalt'
}

export function gripFor(surface: Surface): number { return SURFACE_GRIP[surface] }
export function accelFor(surface: Surface): number { return SURFACE_ACCEL[surface] }

/**
 * Grip at a point, blended across segment boundaries over
 * {@link SURFACE_TRANSITION_M} centred on the seam — see that constant for why.
 *
 * Deliberately NOT `gripFor(getSurfaceAt(d))`: surface identity stays a hard
 * edge (visuals, drag, fuel, audio all key off it), only the number moves.
 */
export function getGripAt(distanceMeters: number): number {
  if (distanceMeters < 0) return SURFACE_GRIP.asphalt

  const half = SURFACE_TRANSITION_M / 2
  ensureGenerated(distanceMeters + half)

  let i = -1
  for (let k = _segments.length - 1; k >= 0; k--) {
    if (distanceMeters >= _segments[k]!.start) { i = k; break }
  }
  if (i < 0) return SURFACE_GRIP.asphalt

  const seg = _segments[i]!
  const here = SURFACE_GRIP[seg.surface]

  // Entering: blend up from the previous segment. The first segment has no
  // predecessor, so it starts flat.
  if (i > 0 && distanceMeters < seg.start + half) {
    const prev = SURFACE_GRIP[_segments[i - 1]!.surface]
    const t = (distanceMeters - (seg.start - half)) / SURFACE_TRANSITION_M
    return prev + (here - prev) * smoothstep(Math.max(0, Math.min(1, t)))
  }

  // Leaving: blend down into the next segment.
  const next = _segments[i + 1]
  if (next && distanceMeters > seg.end - half) {
    const there = SURFACE_GRIP[next.surface]
    const t = (distanceMeters - (seg.end - half)) / SURFACE_TRANSITION_M
    return here + (there - here) * smoothstep(Math.max(0, Math.min(1, t)))
  }

  return here
}

export interface DangerAhead {
  surface: Surface
  /** Metres from the player to where it starts. */
  distanceM: number
  /**
   * Sharpest signed curvature inside the hazard within the look-ahead window.
   * Negative left, positive right, 0 if the visible part of it is straight.
   */
  curvature: number
}

/**
 * Nearest different dangerous surface within {@link ICE_AHEAD_LOOK_M}, with how
 * far off it is and whether it bends. Silent when already on it.
 *
 * Scans the interval rather than sampling the single point at the far end of it,
 * which is what this used to do. That point sample was silent about any hazard
 * shorter than the look-ahead — the segment could open and close between the
 * player and the probe — and it could only ever say "something is coming", never
 * how far, so the strip read the same at 220 m as at 10 m.
 */
export function isDangerAhead(currentDist: number): DangerAhead | null {
  const current = getSurfaceAt(currentDist)
  const end = currentDist + ICE_AHEAD_LOOK_M
  const STEP = 5

  // Walk past the surface underfoot first: more of what you are already on is
  // not news, and neither is the asphalt in between.
  let d = currentDist
  while (d <= end && getSurfaceAt(d) === current) d += STEP
  while (d <= end && getSurfaceAt(d) === 'asphalt') d += STEP
  if (d > end) return null

  const surface = getSurfaceAt(d)
  const startsAt = d

  // How the hazard bends, as far into it as the player can see.
  let curvature = 0
  for (; d <= end && getSurfaceAt(d) === surface; d += STEP) {
    const c = getCurvatureAt(d)
    if (Math.abs(c) > Math.abs(curvature)) curvature = c
  }

  return { surface, distanceM: startsAt - currentDist, curvature }
}

export interface CurveAhead {
  /** Metres to where the bend reaches warning sharpness. 0 means you are in it. */
  distanceM: number
  /** Signed curvature there — negative left, positive right. */
  curvature: number
}

/**
 * Sharp bend ahead, but only while the truck is on a surface that cannot simply
 * be steered through — see {@link CURVE_WARN_GRIP_MAX}.
 *
 * This covers the gap {@link isDangerAhead} leaves by design: that one goes quiet
 * once you are standing on the hazard, and a 2.0 bend in the middle of a long ice
 * run is exactly where quiet is wrong. Surfaces and curves are generated
 * independently, so this case is common rather than exotic.
 *
 * Keys off surface *identity*, not {@link getGripAt}: the blended value would
 * cross the threshold somewhere inside a 20 m seam and make the strip flicker on
 * and off as the truck crawled across it.
 */
export function sharpCurveAhead(currentDist: number): CurveAhead | null {
  if (currentDist < 0) return null
  if (SURFACE_GRIP[getSurfaceAt(currentDist)] > CURVE_WARN_GRIP_MAX) return null

  const end = currentDist + CURVE_AHEAD_LOOK_M
  for (let d = currentDist; d <= end; d += 5) {
    const c = getCurvatureAt(d)
    if (Math.abs(c) >= CURVE_WARN_CURVATURE) {
      return { distanceM: d - currentDist, curvature: c }
    }
  }
  return null
}

// ── Curvature pattern: straight → ramp → turn → ramp → straight ────────────

interface CurveSection {
  start: number
  end: number
  /** 0 = straight, non-zero = full curvature (negative=left, positive=right). */
  curvature: number
  /** 'straight' | 'rampIn' | 'turn' | 'rampOut' */
  type: 'straight' | 'rampIn' | 'turn' | 'rampOut'
}

let _curves: CurveSection[] = []
let _curvesUpTo = 0

function ensureCurvesGenerated(upToDist: number): void {  // uses _seed from module scope
  while (_curvesUpTo < upToDist + 500) {
    const idx = _curves.length

    if (idx === 0) {
      const len = 200
      _curves.push({ start: 0, end: len, curvature: 0, type: 'straight' })
      _curvesUpTo = len
      continue
    }

    const prev = _curves[idx - 1]!

    if (prev.type === 'straight' || prev.type === 'rampOut') {
      // After a straight or ramp-out → start a new turn sequence
      // First: straight section
      if (prev.type === 'rampOut') {
        const [minS, maxS] = STRAIGHT_LENGTH_RANGE
        const straightLen = minS + (maxS - minS) * hash(idx * 41 + 5 + _seed)
        _curves.push({ start: _curvesUpTo, end: _curvesUpTo + straightLen, curvature: 0, type: 'straight' })
        _curvesUpTo += straightLen
        continue
      }

      // After a straight → rampIn to a turn
      const [minI, maxI] = CURVE_INTENSITY_RANGE
      const intensity = minI + (maxI - minI) * hash(idx * 59 + 13 + _seed)
      const direction = hash(idx * 73 + 29 + _seed) < 0.5 ? -1 : 1
      const curvature = intensity * direction

      _curves.push({ start: _curvesUpTo, end: _curvesUpTo + TURN_RAMP_M, curvature, type: 'rampIn' })
      _curvesUpTo += TURN_RAMP_M
      continue
    }

    if (prev.type === 'rampIn') {
      // rampIn → full turn
      const [minT, maxT] = TURN_LENGTH_RANGE
      const turnLen = minT + (maxT - minT) * hash(idx * 47 + 17 + _seed)
      _curves.push({ start: _curvesUpTo, end: _curvesUpTo + turnLen, curvature: prev.curvature, type: 'turn' })
      _curvesUpTo += turnLen
      continue
    }

    if (prev.type === 'turn') {
      // turn → rampOut
      _curves.push({ start: _curvesUpTo, end: _curvesUpTo + TURN_RAMP_M, curvature: prev.curvature, type: 'rampOut' })
      _curvesUpTo += TURN_RAMP_M
      continue
    }
  }
}

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t)
}

export function getCurvatureAt(distM: number): number {
  if (distM < 0) return 0
  ensureCurvesGenerated(distM)

  for (let i = _curves.length - 1; i >= 0; i--) {
    const sec = _curves[i]!
    if (distM < sec.start) continue

    switch (sec.type) {
      case 'straight':
        return 0
      case 'turn':
        return sec.curvature
      case 'rampIn': {
        const t = (distM - sec.start) / (sec.end - sec.start)
        return sec.curvature * smoothstep(Math.min(1, t))
      }
      case 'rampOut': {
        const t = (distM - sec.start) / (sec.end - sec.start)
        return sec.curvature * (1 - smoothstep(Math.min(1, t)))
      }
    }
  }
  return 0
}
